/* eslint-disable react/no-access-state-in-setstate */
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import isNil from 'lodash/isNil';
import orderBy from 'lodash/orderBy';
import formatDate from 'date-fns/format';
import CommonStore from '@audacious/web-common/fluxible/CommonStore';
import formatPatient from '../common/util/format-patient';

const initialState = {
    step: null,
    searchData: {
        running: false,
        results: null,
        original: null,
    },
    selectedPatients: {},
    isCommitting: false,
    sortParams: {
        sortPath: null,
        descending: false,
    }
};

function sort(items, sortParams) {
    const {
        sortPath,
        descending,
    } = sortParams;

    if (isNil(sortPath)) {
        return [...items];
    }

    const order = descending ? 'desc' : 'asc';

    return orderBy(items, [sortPath], [order]);
}

class CheckOutWorkflowStore extends CommonStore {
    constructor(dispatcher) {
        super(dispatcher, initialState);
    }

    startCheckOut() {
        this.setState({
            ...initialState,
            step: 'started',
            isCommitting: false,
        });
    }

    patientSearchStart() {
        if (this.state.step !== 'started') {
            throw new Error('Invalid check out step');
        }

        this.setState({
            ...this.state,
            searchData: {
                ...this.state.searchData,
                running: true,
                error: null,
            },
        });
    }

    patientSearchFinish({ results }) {
        const { checkins } = results;

        if (this.state.step !== 'started') {
            throw new Error('Invalid check out step');
        }

        const original = map(checkins, patient => 
            // eslint-disable-next-line no-param-reassign
             ({
                id: patient.mrn,
                patient,
                formatted: formatPatient(patient),
            })
        );

        this.setState({
            ...this.state,
            searchData: {
                ...this.state.searchData,
                running: false,
                error: null,
                original,
                results: sort(original, this.state.sortParams),
            },
        });
    }

    patientSearchFail(error) {
        if (this.state.step !== 'started') {
            throw new Error('Invalid check out step');
        }

        this.setState({
            ...this.state,
            searchData: {
                ...this.state.searchData,
                running: false,
                error,
            },
        });
    }

    startDestination(selectedPatients) {
        this.setState({
            ...this.state,
            step: 'destination',
            selectedPatients,
        });
    }

    cancelDestination() {
        this.setState({
            ...this.state,
            step: 'started',
        });
    }

    startReview(selectedPatients) {
        const patients = mapValues(selectedPatients, patient => {
            const {
                destination: { deceased },
            } = patient;

            return {
                ...patient,
                destination: {
                    ...patient.destination,
                    deceasedFormatted: !isNil(deceased)
                        ? formatDate(deceased, 'MM/dd/yyyy')
                        : null,
                },
            };
        });

        this.setState({
            ...this.state,
            step: 'review',
            selectedPatients: patients,
        });
    }

    cancelReview() {
        this.setState({
            ...this.state,
            step: 'destination',
        });
    }

    startConfirmation() {
        this.setState({
            ...this.state,
            step: 'confirmation',
        });
    }

    checkOutPatientsStart() {
        this.setState({
            isCommitting: true,
        });
    }

    checkOutPatientsFinish([error]) {
        const newState = {
            error,
            isCommitting: false,
        };

        if (isNil(error)) {
            newState.step = null;
        }

        this.setState(newState);
    }

    getWorkflowStep() {
        return this.getState().step;
    }

    getSearchData() {
        return this.state.searchData;
    }

    getSelectedPatients() {
        return this.state.selectedPatients;
    }

    isCommitting() {
        return this.state.isCommitting;
    }

    getError() {
        return this.state.error;
    }

    setSortParams(sortParams) {
        const { searchData } = this.state;

        this.setState({
            sortParams,
            searchData: {
                ...searchData,
                results: sort(searchData.original, sortParams),
            },
        });
    }

    getSortParams() {
        return this.state.sortParams;
    }
}

CheckOutWorkflowStore.storeName = 'CheckOutWorkflowStore';
CheckOutWorkflowStore.handlers = {
    CHECK_OUT_START: 'startCheckOut',
    CHECK_OUT_START_DESTINATION: 'startDestination',
    CHECK_OUT_CANCEL_DESTINATION: 'cancelDestination',
    CHECK_OUT_START_REVIEW: 'startReview',
    CHECK_OUT_CANCEL_REVIEW: 'cancelReview',
    CHECK_OUT_START_CONFIRMATION: 'startConfirmation',
    CHECK_OUT_PATIENTS_START: 'checkOutPatientsStart',
    CHECK_OUT_PATIENTS_FINISH: 'checkOutPatientsFinish',
    CHECK_OUT_SEARCH_START: 'patientSearchStart',
    CHECK_OUT_SEARCH_SUCCESS: 'patientSearchFinish',
    CHECK_OUT_SEARCH_FAILED: 'patientSearchFail',
    CLEAR_STORES: 'resetState',
    LOGOUT: 'resetState',
    CHECK_OUT_SET_SORT_PARAMS: 'setSortParams',
};

export default CheckOutWorkflowStore;
